import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService, UserService } from '#services';
import { AccessStatus } from '#enums';

function validateAccess(authService: AuthService, url: string) {
  const accessStatus = authService.getAccessStatus();
  if (accessStatus === AccessStatus.None && !url.includes('/user/request-access')) {
    return {
      isSuccess: false,
      url: '/user/request-access',
    };
  }

  if (accessStatus === AccessStatus.Pending && !url.includes('/user/pending-access')) {
    return {
      isSuccess: false,
      url: '/user/pending-access',
    };
  }

  if (accessStatus === AccessStatus.Granted && url.includes('-access')) {
    return {
      isSuccess: false,
      url: '/',
    };
  }

  return { isSuccess: true, url: '' };
}

export const authGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const userService = inject(UserService);

  try {
    if (authService.isLoginInProgress()) {
      return false;
    }

    if (!authService.isAuthenticated()) {
      await authService.login();
      return false;
    }

    // Only fetch user details if not already cached
    if (!authService.getUserDetails()) {
      const userDetails = await firstValueFrom(userService.getCurrentUser());
      authService.setUserDetails(
        userDetails !== null
          ? {
            id: userDetails.ID,
            email: userDetails.Email,
            hasAccess: userDetails.HasAccess,
            isBuAdmin: userDetails.IsBUAdmin,
            isSysAdmin: userDetails.IsSystemAdmin,
            isOrgAdmin: userDetails.IsOrgAdmin,
            username: authService.getAccountName(),
          }
          : null
      );
    }

    const validateAccessResult = validateAccess(authService, state.url);
    if (!validateAccessResult.isSuccess) {
      router.navigate([validateAccessResult.url], { queryParams: { returnUrl: state.url } });
      return false;
    }

    if (!('roles' in route.data)) {
      return true;
    }

    const isAuthorized = authService.isAuthorized(route.data['roles']);
    if (!isAuthorized) {
      router.navigate(['/403'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    return true;
  } catch (err) {
    // 401/403: token expired — logout() in the interceptor already triggered a redirect; cancel this navigation
    if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
      return false;
    }
    // MSAL / token errors are transient — send to the login flow, not the server error page
    const isServerError = err instanceof HttpErrorResponse;
    return router.navigate([isServerError ? '/500' : '/403'], { queryParams: { returnUrl: state.url } });
  }
};
